import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from "@angular/core";
import { InvoicePreviewLinesDto } from "../../../core/models/invoice-preview/invoicePreviewLinesDto";
import { InvoicePreviewOrderLineDetailsService } from "../../../core/services/invoice-preview-order-line-details.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import buildQuery from "odata-query";
import { InvoicePreviewLines } from "../../../core/models/invoice-preview/InvoicePreviewLines";
import { GenericResponseDto } from "../../../core/models/generic-response.dto";
import { PoEmailData } from "../../../core/models/orders/PoEmailData";
import { VendorInvoiceJournalDetailValue } from "../../../core/models/orders/vendorInvoiceJournalDetailValue";
import { GetUserDto } from "../../../core/models/users/users/getUserDto";
import { ValidateLogin } from "../../../core/models/validate-login.dto";
import { InvoicePreviewSummaryService } from "../../../core/services/invoice-preview-summary.service";
import { LoaderService } from "../../../core/services/loader.service";
import { OrderSummaryService } from "../../../core/services/order-summary.service";
import { ProfileFormService } from "../../../core/services/profile-form.service";
import { exportExcel } from "../../../core/services/shared.service";
import { EnumOrderEmailState } from "../../../shared/enums/orderEmailState.enum";
import { UserRole } from "../../../shared/enums/userRoles.enum";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { UserManagementService } from "../../../core/services/user-management.service";
import { TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { HideEmptyDatePipe } from "../../../shared/pipes/hidedate/hide-empty-date.pipe";
import { UnmatchUrlDialogComponent } from "../../orders/components/unmatch-url-dialog/unmatch-url-dialog.component";
import { ModalStackerComponent } from "../../../shared/components/modal-stacker/modal-stacker.component";
import { PoEmailComponent } from "../../orders/components/po-email/po-email.component";

@UntilDestroy()
@Component({
  selector: "jni-invoice-preview-order-line-details",
  templateUrl: "./invoice-preview-order-line-details.component.html",
  styleUrls: ["./invoice-preview-order-line-details.component.scss"],
  imports: [
    TableModule,
    CommonModule,
    HideEmptyDatePipe,
    UnmatchUrlDialogComponent,
    ModalStackerComponent,
    PoEmailComponent,
  ],
})
export class InvoicePreviewOrderLineDetailsComponent
  implements OnInit, OnDestroy
{
  showSmallLoader = false;
  vendorInvoiceJournalDetail: VendorInvoiceJournalDetailValue[] = [];
  unmatchQuantityStatus = false;
  descriptionCharacterCond = false;
  @Input() invoicePreviewNumber = "";
  @Input() orderLineDetailsGridListing: InvoicePreviewLinesDto[] = [];
  @Output() vendorInvoiceEvent = new EventEmitter<string>();
  @Output() scrollHiddenStatus = new EventEmitter();
  @Input() invoicePreviewLines: InvoicePreviewLines = {} as InvoicePreviewLines;
  poEmailData: PoEmailData = {} as PoEmailData;
  poEmailDisplayModal = false;
  @Input() orderLineDetailsConsumesMessage = "No order line found.";
  @Input() isFromIpSummary = false;
  userData!: GetUserDto;
  showNetCommField = false;

  constructor(
    private _invoicePreviewOrderLineDetailsService: InvoicePreviewOrderLineDetailsService,
    private _loaderService: LoaderService,
    private _invoicePreviewSummaryService: InvoicePreviewSummaryService,
    private _orderSummaryService: OrderSummaryService,
    private _toastr: JniToastrService,
    private _profileFormService: ProfileFormService,
    private _loginService: UserManagementService
  ) {}

  ngOnInit(): void {
    this.getUserProfile();
    this.getOrderLineDetails(this.invoicePreviewNumber);
  }

  getOrderLineDetails(invoicePreviewNumber: string) {
    if (invoicePreviewNumber) {
      this._loaderService.setLoaderState(true);
      this._invoicePreviewSummaryService
        .getOrderLineDetails(invoicePreviewNumber)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.data) {
              this.invoicePreviewLines = res.data;
              if (this.invoicePreviewLines?.invoicePreviewLinesDto) {
                this.orderLineDetailsGridListing =
                  this.invoicePreviewLines.invoicePreviewLinesDto ?? null;
              }
            }
          },
          error: () => {
            this._loaderService.setLoaderState(false);
          },
        });
    }
  }

  openUrlDialog(poNumber: string) {
    if (poNumber) {
      const filter = {
        PurchId: poNumber,
      };
      const query = buildQuery({ filter });
      this.vendorInvoiceJournalDetail = [];
      this._loaderService.setLoaderState(true);
      this._orderSummaryService
        .getUnmatchQuantityUrl(query)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.data) {
              if (res.data.length > 0) {
                this.vendorInvoiceJournalDetail = res.data;
                if (this.vendorInvoiceJournalDetail.length === 1) {
                  const url =
                    this.vendorInvoiceJournalDetail[0].DocumentURL || "";
                  window.open(url, "_blank");
                } else {
                  this.unmatchQuantityStatus = true;
                }
              } else {
                this._toastr.error("Error", "Record not found");
              }
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error("Error", res.message || "Record not found");
          },
        });
    } else {
      this._toastr.error("Error", "PO Number is required");
    }
  }

  closeUrlDialog() {
    this.unmatchQuantityStatus = false;
  }

  exportExcel() {
    exportExcel(this.orderLineDetailsGridListing, "OrderLine Details");
  }

  onLineSelection(salesId: string, projId: string) {
    this._invoicePreviewOrderLineDetailsService.onSalesIdSelection({
      SalesId: salesId,
      ProjId: projId,
    });
  }

  onVendorInvoiceSelection() {
    this.vendorInvoiceEvent.emit();
  }

  ngOnDestroy(): void {
    this.scrollHiddenStatus.emit();
  }

  OpenPoPdf(obj: InvoicePreviewLinesDto) {
    this.poEmailData.saleOrderId = obj.salesId;
    this.poEmailData.poNumber = obj.purchaseOrderNumber;
    this.poEmailData.windowState = EnumOrderEmailState.PoPrinting;
    this.poEmailDisplayModal = true;
  }

  closeModalForPoEmail() {
    this.poEmailDisplayModal = false;
  }

  descriptionCharacterCount(obj: string) {
    if (obj.length > 45) this.descriptionCharacterCond = true;
  }

  getUserProfile() {
    this._loginService
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: async res => {
        if (res?.data) {
          this.userData = res?.data;
          if (
            this.userData?.userRoleId === UserRole.Admin ||
            this.userData?.title?.toLowerCase()?.includes("branding consultant")
          ) {
            this.showNetCommField = true;
          } else {
            this.showNetCommField = false;
          }
        }
      },
    });
  }
}
